import React from "react";
import { Link, useParams } from "react-router-dom";
import {
  Card,
  CardHeader,
  CardContent,
  Box,
  Typography,
  Stack,
  IconButton,
  Divider,
  alpha,
  useTheme,
  Chip,
  CircularProgress,
} from "@mui/material";
import {
  MedicalInformation as MedicalIcon,
  ArrowForwardIos as ArrowIcon,
  History as HistoryIcon,
  NotificationsActive as AlertIcon,
  MonitorWeight as WeightIcon,
  WaterDrop as FluidIcon,
  Height as HeightIcon,
} from "@mui/icons-material";
import { useQuery } from "react-query";
import { QK } from "../../../base/qk";
import { getMedicalConditions } from "../../../api/services/Client/medicalConditions";

const ConditionsAlerts = () => {
  const theme = useTheme();
  const { id: clientID } = useParams();

  const { data: conditions, isLoading } = useQuery(
    [QK.medicalConditions, clientID],
    () => getMedicalConditions(clientID),
    {
      enabled: !!clientID,
    }
  );

  if (isLoading) {
    return (
      <Card
        sx={{
          borderRadius: "12px",
          boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.05)",
          overflow: "hidden",
        }}
      >
        <CardContent sx={{ display: "flex", justifyContent: "center", py: 3 }}>
          <CircularProgress size={24} />
        </CardContent>
      </Card>
    );
  }

  const checklist = conditions?.checklist || [];
  const medicalHistory = conditions?.medical_history || "";
  const vitalTargets = conditions?.vital_targets || [];
  const mappingData = conditions?.mapping_data || {};

  // Count enabled conditions from mapping_data
  const getConditionCount = () => {
    let count = 0;
    Object.keys(mappingData).forEach(key => {
      if (mappingData[key]?.enabled === true) count++;
    });
    return count;
  };

  // Count alerts from mapping_data where enabled is true
  const getAlertCount = () => {
    let count = 0;
    Object.keys(mappingData).forEach(key => {
      if (mappingData[key]?.enabled === true) count++;
    });
    return count;
  };

  const getHeight = () => {
    const height = vitalTargets.find(v => v.name === "Height");
    return height?.value || "N/A";
  };

  return (
    <Card
      sx={{
        borderRadius: "12px",
        boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.05)",
        overflow: "hidden",
      }}
    >
      <CardHeader
        sx={{
          px: 2,
          py: 1.5,
          bgcolor: "#FAFBFC",
          borderBottom: "1px solid #F2F4F7",
        }}
        title={
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Box
                sx={{
                  width: 28,
                  height: 28,
                  borderRadius: "6px",
                  bgcolor: alpha(theme.palette.error.main, 0.08),
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <MedicalIcon sx={{ color: theme.palette.error.main, fontSize: 16 }} />
              </Box>
              <Typography variant="subtitle2" fontWeight={600} sx={{ color: "#1A1A2E", fontSize: "0.85rem" }}>
                Conditions & Alerts
              </Typography>
              <Chip
                label={getConditionCount()}
                size="small"
                sx={{
                  height: 18,
                  fontSize: "0.55rem",
                  fontWeight: 500,
                  bgcolor: "#F2F4F7",
                  color: "#667085",
                }}
              />
            </Box>
            <IconButton
              component={Link}
              to={`/conditions-and-alerts/${clientID}`}
              size="small"
              sx={{
                width: 28,
                height: 28,
                bgcolor: "#fff",
                border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                "&:hover": {
                  bgcolor: theme.palette.primary.main,
                  color: "#fff",
                },
                transition: "all 0.2s",
              }}
            >
              <ArrowIcon sx={{ fontSize: 9 }} />
            </IconButton>
          </Box>
        }
      />

      <Divider sx={{ opacity: 0.4 }} />

      <CardContent sx={{ p: 1.5 }}>
        <Stack spacing={0.75}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              py: 0.5,
              px: 0.5,
              borderRadius: "4px",
              "&:hover": {
                bgcolor: alpha(theme.palette.primary.main, 0.03),
              },
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Box
                sx={{
                  width: 20,
                  height: 20,
                  borderRadius: "4px",
                  bgcolor: alpha(theme.palette.error.main, 0.06),
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <MedicalIcon sx={{ fontSize: 12, color: theme.palette.error.main }} />
              </Box>
              <Typography
                variant="caption"
                sx={{
                  color: "#1A1A2E",
                  fontWeight: 500,
                  fontSize: "0.75rem",
                }}
              >
                Medical Conditions
              </Typography>
            </Box>
            <Typography
              variant="caption"
              sx={{
                color: "#667085",
                fontSize: "0.7rem",
                fontWeight: 500,
              }}
            >
              {checklist.length}
            </Typography>
          </Box>

          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              py: 0.5,
              px: 0.5,
              borderRadius: "4px",
              "&:hover": {
                bgcolor: alpha(theme.palette.primary.main, 0.03),
              },
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Box
                sx={{
                  width: 20,
                  height: 20,
                  borderRadius: "4px",
                  bgcolor: alpha(theme.palette.info.main, 0.06),
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <HistoryIcon sx={{ fontSize: 12, color: theme.palette.info.main }} />
              </Box>
              <Typography
                variant="caption"
                sx={{
                  color: "#1A1A2E",
                  fontWeight: 500,
                  fontSize: "0.75rem",
                }}
              >
                Past Medical History
              </Typography>
            </Box>
            <Typography
              variant="caption"
              sx={{
                color: "#667085",
                fontSize: "0.7rem",
                fontWeight: 500,
              }}
            >
              {medicalHistory ? "Yes" : "No"}
            </Typography>
          </Box>

          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              py: 0.5,
              px: 0.5,
              borderRadius: "4px",
              "&:hover": {
                bgcolor: alpha(theme.palette.primary.main, 0.03),
              },
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Box
                sx={{
                  width: 20,
                  height: 20,
                  borderRadius: "4px",
                  bgcolor: alpha(theme.palette.warning.main, 0.06),
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <AlertIcon sx={{ fontSize: 12, color: theme.palette.warning.main }} />
              </Box>
              <Typography
                variant="caption"
                sx={{
                  color: "#1A1A2E",
                  fontWeight: 500,
                  fontSize: "0.75rem",
                }}
              >
                Alerts
              </Typography>
            </Box>
            <Typography
              variant="caption"
              sx={{
                color: "#667085",
                fontSize: "0.7rem",
                fontWeight: 500,
              }}
            >
              {getAlertCount()} set up
            </Typography>
          </Box>

          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              py: 0.5,
              px: 0.5,
              borderRadius: "4px",
              "&:hover": {
                bgcolor: alpha(theme.palette.primary.main, 0.03),
              },
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Box
                sx={{
                  width: 20,
                  height: 20,
                  borderRadius: "4px",
                  bgcolor: alpha(theme.palette.primary.main, 0.06),
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <WeightIcon sx={{ fontSize: 12, color: theme.palette.primary.main }} />
              </Box>
              <Typography
                variant="caption"
                sx={{
                  color: "#1A1A2E",
                  fontWeight: 500,
                  fontSize: "0.75rem",
                }}
              >
                Weight Loss Alert
              </Typography>
            </Box>
            <Typography
              variant="caption"
              sx={{
                color: "#667085",
                fontSize: "0.7rem",
                fontWeight: 500,
              }}
            >
              {conditions?.weight_change_alert_kg || "0"} kg
            </Typography>
          </Box>

          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              py: 0.5,
              px: 0.5,
              borderRadius: "4px",
              "&:hover": {
                bgcolor: alpha(theme.palette.primary.main, 0.03),
              },
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Box
                sx={{
                  width: 20,
                  height: 20,
                  borderRadius: "4px",
                  bgcolor: alpha(theme.palette.primary.main, 0.06),
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <FluidIcon sx={{ fontSize: 12, color: theme.palette.primary.main }} />
              </Box>
              <Typography
                variant="caption"
                sx={{
                  color: "#1A1A2E",
                  fontWeight: 500,
                  fontSize: "0.75rem",
                }}
              >
                Fluid Target
              </Typography>
            </Box>
            <Typography
              variant="caption"
              sx={{
                color: "#667085",
                fontSize: "0.7rem",
                fontWeight: 500,
              }}
            >
              {conditions?.fluid_target_required ? `${conditions?.fluid_target_ml || 0} ml` : "N/A"}
            </Typography>
          </Box>

          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              py: 0.5,
              px: 0.5,
              borderRadius: "4px",
              "&:hover": {
                bgcolor: alpha(theme.palette.primary.main, 0.03),
              },
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Box
                sx={{
                  width: 20,
                  height: 20,
                  borderRadius: "4px",
                  bgcolor: alpha(theme.palette.primary.main, 0.06),
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <HeightIcon sx={{ fontSize: 12, color: theme.palette.primary.main }} />
              </Box>
              <Typography
                variant="caption"
                sx={{
                  color: "#1A1A2E",
                  fontWeight: 500,
                  fontSize: "0.75rem",
                }}
              >
                Height
              </Typography>
            </Box>
            <Typography
              variant="caption"
              sx={{
                color: "#667085",
                fontSize: "0.7rem",
                fontWeight: 500,
              }}
            >
              {getHeight()} m
            </Typography>
          </Box>
        </Stack>
      </CardContent>
    </Card>
  );
};

export default ConditionsAlerts;